import { Box, Grid, lighten, darken, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Table } from '../../components';
import { IAgentCommission, ReactSetState } from '../../models';
import { theme } from '../../styles';

import { MuiTextFieldProps } from '@mui/lab/internal/pickers/PureDateInput';
import { IParentPropertySubType } from '../../models/property-subtypes';

export interface IPropertyTypes {
  propertySubTypes: IParentPropertySubType[];
  setPropertySubTypes: ReactSetState<any[]>;
  propertySubTypesCopy: IParentPropertySubType[];
  selectedPropertySubType: IParentPropertySubType | null;
  setSelectedPropertySubType: ReactSetState<any | null>;
  newElementIndex: number;
  setNewElementIndex: any;
  hasChanges: boolean;
  isLoading: boolean;
}
export const PropertyTypes: FC<IPropertyTypes> = ({
  propertySubTypes,
  setPropertySubTypes,
  selectedPropertySubType,
  setSelectedPropertySubType,
  setNewElementIndex,
  hasChanges,
  isLoading,
}) => {
  // Non-state
  const classes = useStyles();

  // Network state

  // Non-network state

  const [showNewTableNameInput, setShowNewTableNameInput] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>('');
  const [isNewNameError, setIsNewNameError] = useState(false);
  const nameTextBox = useRef<MuiTextFieldProps>(null);

  const nameIsTaken = () => {
    const names = propertySubTypes.map(property => property.propertyTypeName.toLowerCase().trim());
    return names.includes(newName.toLowerCase().trim());
  };

  const nameIsEmpty = () => {
    return newName === ''.trim();
  };

  const nameIsValidated = () => {
    return !nameIsTaken() && !nameIsEmpty();
  };

  const errorMessage = () => {
    if (nameIsTaken()) {
      return 'This name is already taken';
    }
    if (nameIsEmpty()) {
      return 'Name cannot be empty';
    }
  };
  const addNewCommission = (name: string) => {
    const newCommission: IAgentCommission = {
      // Negative number to convert to zero when posting. May not be needed, but adding for explicitness.
      agentCommissionId: -Math.round(Math.random() * 1000000),
      agentCommissionTiers: [],
      name: name,
    };
    if (nameIsValidated()) {
      setPropertySubTypes(prev => [...prev, newCommission]);
      setNewName('');
      setShowNewTableNameInput(false);
      setSelectedPropertySubType(newCommission);
    } else {
      setIsNewNameError(true);
    }
  };

  useEffect(() => {
    if (nameIsValidated()) {
      setIsNewNameError(false);
    } else {
      setIsNewNameError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newName]);

  const propertyNamesColumns = useMemo(() => {
    return [
      {
        id: 'header',
        accessor: '',
        Header: 'PropertyTypes',
        disableSortBy: true,
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: IParentPropertySubType } };
        }) => {
          const row = original;
          return (
            <Box>
              <Grid
                item
                key={row.propertyTypeId}
                sx={theme => ({
                  transition: 'padding 160ms ease-in',
                  borderRadius: 1,
                  padding: theme.spacing(0.465),
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: lighten(theme.frankie.neonYellow, 0.8),
                    paddingInline: theme.spacing(1),
                  },
                  // Display Frankie-themed background if this is the selected table.
                  ...(selectedPropertySubType?.propertyTypeId === row.propertyTypeId
                    ? {
                        backgroundColor: lighten(theme.frankie.neonYellow, 0.6),
                        borderLeft: '5px solid',
                        borderColor: theme.frankie.neonYellow,
                        paddingInline: theme.spacing(1),
                        '.MuiTypography-root': {
                          fontWeight: '700',
                        },
                      }
                    : {}),
                })}
                onClick={() => {
                  setSelectedPropertySubType(row);
                  setNewElementIndex(-1);
                }}
              >
                <Typography
                  sx={theme => ({
                    color: theme.frankie.searchBlue,
                  })}
                >
                  {row.propertyTypeName}
                </Typography>
              </Grid>
            </Box>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertySubType?.propertyTypeName, setNewElementIndex]);

  return (
    <>
      <Table
        data={propertySubTypes}
        columns={propertyNamesColumns}
        isLoading={isLoading}
        headerClasses={classes.header}
        hidePagination
        hideTableHeader={true}
      />
      {showNewTableNameInput && (
        <TextField
          label="Name"
          inputRef={nameTextBox}
          className={classes.CommissionTableName}
          fullWidth
          value={newName}
          onBlur={() => addNewCommission(newName)}
          onChange={({ target: { value } }) => setNewName(value)}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              addNewCommission(newName);
            }
          }}
          autoFocus
          error={isNewNameError}
          helperText={isNewNameError && errorMessage()}
        />
      )}
    </>
  );
};

const useStyles = makeStyles(() => ({
  header: {
    textAlign: 'center',
  },
  addCommissionTableButton: {
    transition: 'padding 160ms ease-in',
    width: '100%',
    color: theme.frankie.searchBlue,
    backgroundColor: theme.palette.grey[300],
    padding: theme.spacing(0.465),
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'left',
    marginTop: 6,
    justifyContent: 'space-around',

    '&:hover': {
      backgroundColor: darken(theme.palette.grey[300], 0.1),
      paddingInline: theme.spacing(1, 0.535),
    },
  },
  CommissionTableName: {
    margin: theme.spacing(1, 0),
  },
}));
